import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { logAudit } from "@/lib/logAudit";
import LoadingState from "@/components/base/LoadingState";

type ContractSignature = {
  id: string;
  signer_name: string;
  signer_email: string | null;
  signer_phone: string | null;
  signature_data: string;
  signed_at: string;
  ip_address: string | null;
  user_agent: string | null;
};

type Contract = {
  id: string;
  tenant_id: string;
  title: string;
  contract_number: string;
  signature_status: string | null;
  client_signature: string | null;
  signed_at: string | null;
  signature_ip: string | null;
  status: string;
};

interface Props {
  contract: Contract;
  tenantId: string;
  onUpdated: (updated: Partial<Contract>) => void;
}

export default function SignatureTab({ contract, tenantId, onUpdated }: Props) {
  const isSigned = contract.signature_status === "signed";
  const [signatures, setSignatures] = useState<ContractSignature[]>([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    if (!contract.id || !isSigned) return;
    setLoading(true);
    supabase
      .from("contract_signatures")
      .select("id, signer_name, signer_email, signer_phone, signature_data, signed_at, ip_address, user_agent")
      .eq("contract_id", contract.id)
      .eq("tenant_id", tenantId)
      .order("signed_at", { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          setSignatures(data as ContractSignature[]);
        }
        setLoading(false);
      });
  }, [contract.id, tenantId, isSigned]);

  const formatDate = (d: string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const handleResetSignature = async () => {
    setResetting(true);

    // Delete signatures from contract_signatures
    await supabase
      .from("contract_signatures")
      .delete()
      .eq("contract_id", contract.id)
      .eq("tenant_id", tenantId);

    // Reset contract status
    const { error } = await supabase
      .from("contracts")
      .update({
        signature_status: "pending",
        client_signature: null,
        signed_at: null,
        signature_ip: null,
        status: "active",
        updated_at: new Date().toISOString(),
      })
      .eq("id", contract.id)
      .eq("tenant_id", tenantId);

    if (!error) {
      onUpdated({
        signature_status: "pending",
        client_signature: null,
        signed_at: null,
        signature_ip: null,
        status: "active",
      });
      setSignatures([]);
      logAudit({
        tenantId,
        action: "contract_signed_reset",
        entity: "contract",
        entityId: contract.id,
        entityName: `${contract.contract_number} — signature reset`,
      }).catch(() => {});
    }
    setResetting(false);
    setConfirmReset(false);
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* Status card */}
      <div className={`rounded-xl border p-6 ${isSigned ? "bg-emerald-400/5 border-emerald-400/20" : "bg-white rounded-xl border border-slate-200"}`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${isSigned ? "bg-emerald-400/15" : "bg-white/5"}`}>
            <i className={`text-2xl ${isSigned ? "ri-checkbox-circle-fill text-emerald-400" : "ri-time-line text-slate-400"}`}></i>
          </div>
          <div className="flex-1">
            <p className={`font-semibold text-base mb-1 ${isSigned ? "text-emerald-400" : "text-slate-500"}`}>
              {isSigned ? "Contract Signed" : "Awaiting Signature"}
            </p>
            <p className="text-slate-400 text-sm">
              {isSigned
                ? `This contract has ${signatures.length} digital signature${signatures.length > 1 ? "s" : ""} on record.`
                : "This contract has not been signed yet. Share the client portal link for the client to sign."}
            </p>
          </div>
        </div>
      </div>

      {/* Digital Signatures section */}
      {isSigned && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider">Digital Signatures</h3>

          {loading && <LoadingState />}

          {!loading && signatures.length === 0 && (
            <div className="text-center py-6">
              <p className="text-slate-400 text-sm">No signature records found in the database.</p>
              <p className="text-slate-300 text-xs mt-1">Legacy signature data may still be stored on the contract.</p>
            </div>
          )}

          {!loading && signatures.length > 0 && (
            <div className="space-y-4">
              {signatures.map((sig) => (
                <div key={sig.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                  {/* Signer info row */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-400/10 flex-shrink-0">
                        <i className="ri-shield-check-line text-emerald-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-[#0f172a] font-semibold text-sm">{sig.signer_name}</p>
                        {sig.signer_email && (
                          <p className="text-slate-400 text-xs">{sig.signer_email}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex-shrink-0">
                      Signed
                    </span>
                  </div>

                  {/* Signature preview */}
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-1">Signature</p>
                    <p className="text-lg" style={{ fontFamily: "'Georgia', serif", color: "#9a7840" }}>
                      {sig.signature_data}
                    </p>
                  </div>

                  {/* Metadata grid */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Date</p>
                      <p className="text-slate-600">{formatDate(sig.signed_at)}</p>
                    </div>
                    {sig.ip_address && (
                      <div>
                        <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">IP Address</p>
                        <p className="text-slate-500 font-mono">{sig.ip_address}</p>
                      </div>
                    )}
                    {sig.signer_phone && (
                      <div>
                        <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Phone</p>
                        <p className="text-slate-600">{sig.signer_phone}</p>
                      </div>
                    )}
                    {sig.user_agent && (
                      <div className="col-span-2">
                        <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">Device</p>
                        <p className="text-slate-400 truncate" title={sig.user_agent}>{sig.user_agent}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Legal notice */}
          <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-shield-check-line text-[#C8A96E] text-sm"></i>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              These digital signatures were captured through the secure client portal. Each signature, timestamp, and IP address are stored as evidence of the client&apos;s agreement to the contract terms.
            </p>
          </div>

          {/* Reset signature */}
          <div className="pt-2 border-t border-slate-200">
            {!confirmReset ? (
              <button
                onClick={() => setConfirmReset(true)}
                className="flex items-center gap-2 text-xs text-slate-400 hover:text-red-400 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Reset signature (requires re-signing)
              </button>
            ) : (
              <div className="flex items-center gap-3 bg-red-400/5 border border-red-400/20 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line text-red-400 text-sm"></i>
                <p className="text-slate-500 text-xs flex-1">
                  This will clear all signatures and set the contract back to &quot;Active&quot;. Are you sure?
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setConfirmReset(false)}
                    className="text-xs text-slate-400 hover:text-[#0f172a] border border-white/10 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResetSignature}
                    disabled={resetting}
                    className="flex items-center gap-1.5 text-xs bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {resetting ? <><i className="ri-loader-4-line animate-spin"></i> Resetting…</> : "Yes, Reset"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending state — instructions */}
      {!isSigned && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h3 className="text-slate-400 text-xs uppercase tracking-wider">How to get it signed</h3>
          <div className="space-y-3">
            {[
              { icon: "ri-user-line", text: "Make sure the client has access to their portal link." },
              { icon: "ri-file-text-line", text: "The contract text must be added before the client can sign." },
              { icon: "ri-pen-nib-line", text: "The client signs directly in the portal using their typed name." },
              { icon: "ri-checkbox-circle-line", text: "Once signed, this tab will show the signature and timestamp." },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 flex-shrink-0">
                  <i className={`${step.icon} text-[#C8A96E] text-sm`}></i>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}